import React, { useEffect, useMemo } from "react";
import { AnalyticsViewConfigFunnel } from "@hypertune/shared-internal/src/types";
import { DbFunnelData } from "@hypertune/shared-internal";
import { CommitContext, TimeRange } from "../../../lib/types";
import Funnel from "./Funnel";
import ErrorMessageCard from "../../../components/ErrorMessageCard";
import { useAppDispatch } from "../../../app/hooks";
import { setConfig, HypothesisVariant } from "./analyticsSlice";
import { TestStatisticsDropdownOptions } from "../../../generated/hypertune";
import { queryPollIntervalVeryLongMs } from "../../../lib/constants";
import {
  FunnelDataJsonInput,
  useFunnelDataQuery,
} from "../../../generated/graphql";

export default function FunnelView({
  meId,
  canEdit,
  hasError,
  selectedViewId,
  commitContext,
  config,
  timeRange,
  focusModeEnabled,
  selectedMetric,
  selectedHypothesis,
}: {
  meId: string;
  canEdit: boolean;
  hasError: boolean;
  selectedViewId: string;
  commitContext: CommitContext;
  config: AnalyticsViewConfigFunnel;
  timeRange: TimeRange;
  focusModeEnabled: boolean;
  selectedMetric: keyof TestStatisticsDropdownOptions;
  selectedHypothesis: HypothesisVariant;
}): React.ReactElement {
  const dispatch = useAppDispatch();

  const input: FunnelDataJsonInput = useMemo(
    () => ({
      start: timeRange.start.toJSON(),
      end: timeRange.end.toJSON(),
      viewId: selectedViewId,
      configJson: JSON.stringify(config),
    }),
    [timeRange.start, timeRange.end, selectedViewId, config]
  );

  const { loading, error, data, startPolling, stopPolling } =
    useFunnelDataQuery({ variables: { input } });

  useEffect(() => {
    startPolling(queryPollIntervalVeryLongMs);
    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  const dbFunnelData = data
    ? // TODO: Validate JSON with zod
      (JSON.parse(data.funnelDataJson) as DbFunnelData)
    : null;

  return (
    <div className="h-full w-full overflow-auto p-4">
      {loading ||
      (!hasError &&
        dbFunnelData &&
        dbFunnelData.length !== config.steps.length) ? (
        <Funnel.LoadingSkeleton focusModeEnabled={focusModeEnabled} />
      ) : error ? (
        <ErrorMessageCard error={error} />
      ) : dbFunnelData ? (
        <Funnel
          meId={meId}
          canEdit={canEdit}
          commitContext={commitContext}
          steps={config.steps}
          setSteps={(newSteps) => {
            dispatch(setConfig({ type: "funnel", steps: newSteps }));
          }}
          data={dbFunnelData}
          focusModeEnabled={focusModeEnabled}
          selectedMetric={selectedMetric}
          selectedHypothesis={selectedHypothesis}
        />
      ) : null}
    </div>
  );
}
